import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, formatEUR, formatDate } from "@/components/app/Shared";
import { useT } from "@/lib/i18n";
import { Camera, Upload, Trash2, X, Receipt, ScanText } from "lucide-react";
import { toast } from "sonner";
import InvoiceCheck from "@/pages/InvoiceCheck";

export default function Invoices() {
  const { user } = useAuth();
  const t = useT();
  const isChef = user?.role === "chef";
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "controle" ? "controle" : "list";
  const [tab, setTab] = useState(initialTab);
  const switchTab = (next) => {
    setTab(next);
    setSearchParams(next === "controle" ? { tab: "controle" } : {}, { replace: true });
  };
  const [invoices, setInvoices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [image, setImage] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [restaurantId, setRestaurantId] = useState(user?.restaurant_id || "");
  const [restaurants, setRestaurants] = useState([]);
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom] = useState(null);
  const cameraRef = useRef(null);
  const fileRef = useRef(null);

  const load = () => api.get("/invoices").then((r) => setInvoices(r.data));
  useEffect(() => { load(); api.get("/restaurants").then((r) => setRestaurants(r.data)); }, []);

  const onFile = (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Foto te groot (max 4 MB)"); return; }
    const reader = new FileReader();
    reader.onload = (e) => setImage(e.target.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!image) { toast.error("Voeg eerst een foto toe"); return; }
    setSaving(true);
    try {
      await api.post("/invoices", {
        image_url: image,
        description: description || null,
        amount: amount ? parseFloat(amount) : null,
        restaurant_id: restaurantId || null,
      });
      toast.success("Factuur opgeslagen");
      setShowForm(false);
      setImage(""); setDescription(""); setAmount("");
      load();
    } catch (err) {
      toast.error("Opslaan mislukt");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!confirm("Verplaatsen naar prullenbak?")) return;
    await api.delete(`/invoices/${id}`);
    toast.success("Verplaatst naar prullenbak");
    load();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="invoices-page">
      <PageHeader
        overline={t("invoices")}
        title={t("invoices")}
        description={isChef ? "Maak een foto van een factuur of bonnetje na ontvangst, of controleer een factuur tegen een bestelling." : "Overzicht van alle gefotografeerde facturen, gesorteerd per restaurant."}
        actions={tab === "list" && (
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 h-10 px-4 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-full text-sm font-semibold" data-testid="invoice-add-button">
            <Camera size={16} /> {t("add_invoice")}
          </button>
        )}
      />

      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={() => switchTab("list")}
          className={`inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-semibold border transition-colors ${tab === "list" ? "bg-[#101828] text-white border-[#101828]" : "bg-white text-zinc-700 border-zinc-300 hover:border-[#1B2D5C]"}`}
          data-testid="invoices-tab-list">
          <Receipt size={14} /> Facturen
        </button>
        <button onClick={() => switchTab("controle")}
          className={`inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-semibold border transition-colors ${tab === "controle" ? "bg-[#101828] text-white border-[#101828]" : "bg-white text-zinc-700 border-zinc-300 hover:border-[#1B2D5C]"}`}
          data-testid="invoices-tab-controle">
          <ScanText size={14} /> Factuurcontrole (AI)
        </button>
      </div>

      {tab === "controle" ? <InvoiceCheck /> : <>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="invoices-grid">
        {invoices.length === 0 ? (
          <div className="col-span-full py-16 text-center text-zinc-500 border border-dashed border-zinc-200 rounded-md">
            <Receipt size={32} className="mx-auto mb-3 text-zinc-300" />
            Nog geen facturen.
          </div>
        ) : isChef ? invoices.map((inv) => <InvoiceCard key={inv.id} inv={inv} isAdmin={!isChef} onZoom={setZoom} onDel={del} />) : null}
      </div>

      {!isChef && invoices.length > 0 && (
        <div className="space-y-6" data-testid="invoices-grouped">
          {(() => {
            const groups = invoices.reduce((acc, inv) => {
              const k = inv.restaurant_name || "Zonder restaurant";
              (acc[k] ||= []).push(inv);
              return acc;
            }, {});
            return Object.entries(groups).map(([rname, items]) => (
              <section key={rname} data-testid={`invoice-group-${rname.replace(/\s/g, "-")}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="overline">{rname}</div>
                  <div className="flex-1 h-px bg-zinc-200" />
                  <div className="text-xs text-zinc-500 font-mono">{items.length} factuur{items.length === 1 ? "" : "en"}</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {items.map((inv) => <InvoiceCard key={inv.id} inv={inv} isAdmin={!isChef} onZoom={setZoom} onDel={del} />)}
                </div>
              </section>
            ));
          })()}
        </div>
      )}

      </>}

      {showForm && (
        <div className="fixed inset-0 bg-zinc-900/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowForm(false)} data-testid="invoice-form-overlay">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl my-8" onClick={(e) => e.stopPropagation()} data-testid="invoice-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold tracking-tight text-[#1B2D5C]">{t("add_invoice")}</h3>
              <button onClick={() => setShowForm(false)} className="text-zinc-400 p-1"><X size={18} /></button>
            </div>

            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="overline block mb-1.5">Foto</label>
                {image ? (
                  <div className="relative">
                    <img src={image} alt="" className="w-full max-h-72 object-contain rounded-md border border-zinc-200 bg-zinc-50" />
                    <button type="button" onClick={() => setImage("")} className="absolute top-2 right-2 bg-white/90 border border-zinc-200 rounded p-1 text-zinc-600 hover:text-red-600">
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="invoice-camera-input" />
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="invoice-file-input" />
                    <button type="button" onClick={() => cameraRef.current?.click()} className="h-24 border border-dashed border-zinc-300 hover:border-[#5BC044] hover:text-[#5BC044] rounded-md flex flex-col items-center justify-center gap-1 text-sm text-zinc-600" data-testid="invoice-camera-button">
                      <Camera size={20} />
                      Camera
                    </button>
                    <button type="button" onClick={() => fileRef.current?.click()} className="h-24 border border-dashed border-zinc-300 hover:border-[#5BC044] hover:text-[#5BC044] rounded-md flex flex-col items-center justify-center gap-1 text-sm text-zinc-600">
                      <Upload size={20} />
                      Bestand
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="overline block mb-1.5">Omschrijving (optioneel)</label>
                <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Bv. Versgroenten BV - week 23" className="form-input" data-testid="invoice-description" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="overline block mb-1.5">Bedrag (€)</label>
                  <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="form-input" data-testid="invoice-amount" />
                </div>
                <div>
                  <label className="overline block mb-1.5">Restaurant</label>
                  <select value={restaurantId} onChange={(e) => setRestaurantId(e.target.value)} className="form-input" data-testid="invoice-restaurant">
                    <option value="">—</option>
                    {restaurants.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
              </div>

              <button type="submit" disabled={saving || !image} className="w-full h-10 bg-[#5BC044] hover:bg-[#4FAA3A] disabled:opacity-60 text-white rounded-full text-sm font-semibold" data-testid="invoice-submit">
                {saving ? "..." : t("save")}
              </button>
            </form>
            <style>{`.form-input { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #d4d4d8; border-radius: 6px; font-size: 14px; background: white; } .form-input:focus { outline: none; border-color: #5BC044; box-shadow: 0 0 0 3px rgba(91,192,68,0.18); }`}</style>
          </div>
        </div>
      )}

      {zoom && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setZoom(null)} data-testid="invoice-zoom">
          <img src={zoom} alt="" className="max-h-[90vh] max-w-[90vw] object-contain" />
          <button onClick={() => setZoom(null)} className="absolute top-4 right-4 text-white p-2 bg-white/10 rounded"><X size={20} /></button>
        </div>
      )}
    </div>
  );
}

function InvoiceCard({ inv, isAdmin, onZoom, onDel }) {
  return (
    <div className="card-flat overflow-hidden" data-testid={`invoice-${inv.id}`}>
      <button onClick={() => onZoom(inv.image_url)} className="block w-full aspect-[4/5] bg-zinc-100 bg-cover bg-center hover:opacity-90 transition" style={{ backgroundImage: `url(${inv.image_url})` }} />
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm truncate text-[#1B2D5C]">{inv.description || "Factuur"}</div>
            <div className="text-xs text-zinc-500 mt-0.5 truncate">{inv.chef_name}</div>
            <div className="text-xs text-zinc-400 font-mono mt-1">{formatDate(inv.created_at)}</div>
          </div>
          <div className="text-right shrink-0">
            {inv.amount ? <div className="font-mono font-bold text-[#5BC044]">{formatEUR(inv.amount)}</div> : null}
            {isAdmin && (
              <button onClick={() => onDel(inv.id)} className="text-zinc-400 hover:text-red-600 p-1 mt-1" data-testid={`delete-invoice-${inv.id}`}>
                <Trash2 size={13} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
